import { ConfirmModalValues } from '~/components/ui/Modal/ConfirmModalContext.tsx';
import {
  ApiErrorInstance,
  deleteOrganizationById,
  deleteOrganizationDomain,
  getOrganizationsKey,
  getOrganizationsSearchKey,
  isApi5xxResponseStatus,
  OrganizationData,
  withHandleError
} from '~/api';
import { QueryClient } from '@tanstack/solid-query';
import { DomainData, DomainType } from '~/api/types/domainData.ts';
import { getFormattedDate, getLocalizedString } from '~/i18n/utils.ts';
import { HttpStatusCode } from 'axios';
import { ToastContextValue } from '~/components/ui/Toast';
import { ColumnDef } from '@tanstack/solid-table';
import OrganizationListItemActions from '~/pages/Organization/OrganizationListItemActions.tsx';
import { MIN_SEARCH_CHARS } from '~/pages/UnAuthenticated/Login/utils.tsx';
import { ConnectionData, SSOProviders } from '~/api/types/connectionData.ts';
import InfoIcon from '~/components/ui/InfoIcon';
import { Localized } from '~/i18n';
import { Show } from 'solid-js';
import { FeatureFlagKeys } from '~/api/types/featureFlagData.ts';
import { isFeatureEnabled } from '~/utils/features/featureFlag.tsx';
import { AppErrors } from '~/components/ErrorBoundaries/utils.ts';
import { getTranslationKeyFunction } from '~/utils/commonUtils.ts';
import { ReadOnlyField } from '~/components/ui/ReadOnlyField';

const TRANSLATION_BASE_PATH = `pages.organizations`;

const getTranslationKey = (translationStr: string): string =>
  `${TRANSLATION_BASE_PATH}.${translationStr}`;

const getOrgRolesTranslationKey = getTranslationKeyFunction(
  `${TRANSLATION_BASE_PATH}.roles`
);

const ORG_LIST_PAGE_SIZE = 20;

const MIN_MAXIMUM_USERS_LIMIT_IN_ORGANIZATION = 1;

const DEFAULT_MAX_USERS_IN_ORGANIZATION = 25;

enum ORGANIZATION_TAB_TYPES {
  OVERVIEW = 'overview',
  SSO_CONNECTIONS = 'sso',
  DIRECTORY_SYNC = 'scim',
  USERS = 'users',
  ROLES = 'roles'
}

type DeleteOrganisationWithConfirmationParamType = {
  confirm: ConfirmModalValues;
  title: string;
  description: string;
  primaryActionLabel: string;
  org: OrganizationData;
  queryClient: QueryClient;
  toaster: ToastContextValue;
  intl: any;
};

function deleteOrganisationWithConfirmation({
  confirm,
  title,
  description,
  primaryActionLabel,
  org,
  queryClient,
  toaster,
  intl
}: DeleteOrganisationWithConfirmationParamType): Promise<boolean> {
  return new Promise((resolve, reject) => {
    confirm.show({
      title,
      description,
      primaryActionLabel: primaryActionLabel,
      onConfirm: () => {
        confirm.setIsLoading(true);
        deleteOrganizationById(org.id!)
          .then(async () => {
            confirm.setIsLoading(false);
            confirm.close();
            await queryClient.invalidateQueries({
              queryKey: getOrganizationsKey(),
              type: 'all',
              exact: true
            });
            await queryClient.invalidateQueries({
              queryKey: getOrganizationsSearchKey(),
              type: 'all'
            });
            resolve(true);
          })
          .catch((error) => {
            confirm.setIsLoading(false);
            confirm.close();
            toaster.toast().error({
              description: handleDeleteOrganizationErrors({ error, intl })
            });
            // @todo handle error
            reject(error);
          });
      }
    });
  });
}

function getOrgDomainsFromDomainData(domainData: DomainData[]): DomainData[] {
  return domainData.length > 0
    ? domainData.filter(
        (domain) => domain.domainType === DomainType.ORGANIZATION_DOMAIN
      )
    : [];
}

function getAllowedEmailDomainsFromDomainData(
  domainData: DomainData[]
): DomainData[] {
  return domainData.length > 0
    ? domainData.filter(
        (domain) => domain.domainType === DomainType.ALLOWED_EMAIL_DOMAIN
      )
    : [];
}

function shouldSearch(query: string): boolean {
  return query?.length >= MIN_SEARCH_CHARS;
}

const getOrgListColumnDef = ({
  intl,
  onDeleteOrg,
  onEditOrg
}: {
  intl: any;
  onDeleteOrg: (o: string) => void;
  onEditOrg: (o: OrganizationData) => void;
}): ColumnDef<OrganizationData>[] => [
  {
    header: getLocalizedString(`table_headers.organization_id`, { intl }),
    accessorKey: 'id',
    cell: (info) => (
      <ReadOnlyField
        variant={'ghost'}
        value={info.getValue() as string}
      />
    ),
    meta: {
      class: 'w-4/12' // additional meta to control column width
    }
  },
  {
    header: getLocalizedString(`table_headers.organization_name`, { intl }),
    accessorKey: 'displayName',
    cell: (info) => {
      const orgDisplayName = info.getValue() as string;
      return (
        <span class={`flex items-center`}>
          {orgDisplayName || '-'}
          <Show when={isTestOrganization(info.row.original)}>
            <InfoIcon
              class={`ml-1.5 text-fg-muted`}
              tooltipContent={
                <Localized
                  translationKey={getTranslationKey(
                    'org_list_test_org_info_text'
                  )}
                />
              }
            />
          </Show>
        </span>
      );
    },
    meta: {
      class: 'w-4/12' // additional meta to control column width
    }
  },
  {
    header: getLocalizedString(`table_headers.organization_created_date`, {
      intl
    }),
    accessorKey: 'createTime',
    cell: (info) => getFormattedDate(info.getValue() as string),
    meta: {
      class: 'w-3/12' // additional meta to control column width
    }
  },
  {
    header: '',
    id: 'actions',
    accessorKey: 'id',
    cell: (info) => (
      <OrganizationListItemActions
        org={info.row.original}
        onDeleteOrg={onDeleteOrg}
        onEditOrg={onEditOrg}
        disabled={isTestOrganization(info.row.original)}
      />
    ),
    meta: {
      class: 'w-1/12' // additional meta to control column width
    }
  }
];

const isTestOrganization = (org: OrganizationData): boolean => {
  return org.metadata?.testOrganization === 'true';
};

const getTestConnectionFromConnectionList = (connections: ConnectionData[]) => {
  return connections.find((c) => c.provider === SSOProviders.IDP_SIMULATOR);
};

const onDeleteAllowedEmailDomain = async ({
  domain,
  orgId,
  confirm,
  intl,
  toaster,
  refetchDomains
}: {
  domain: DomainData;
  orgId: string;
  confirm: ConfirmModalValues;
  intl: any;
  toaster: ToastContextValue;
  refetchDomains: () => Promise<void>;
}): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    confirm.show({
      title: getLocalizedString(
        getTranslationKey('delete_email_domain_confirm_title'),
        {
          intl
        }
      ),
      description: getLocalizedString(
        getTranslationKey('delete_email_domain_confirm_message'),
        {
          intl,
          domainName: domain.domain,
          bold: (str) => <span class={'font-semibold'}>{str}</span>
        }
      ),
      primaryActionLabel: getLocalizedString('cta_labels.delete', {
        intl
      }),
      primaryActionDataTestId: `org-email-domain-delete`,
      onConfirm: async () => {
        confirm.setIsLoading(true);
        try {
          await deleteOrganizationDomain(orgId, domain.id!);
          await refetchDomains();
          confirm.close();
          toaster.toast().success({
            description: getLocalizedString(
              getTranslationKey('email_domain_delete_success'),
              { intl, domainName: domain.domain }
            )
          });
        } catch (error) {
          confirm.close();
          toaster.toast().error({
            description: handleDeleteOrganizationErrors({
              error: error as ApiErrorInstance,
              intl
            })
          });
          reject(error);
        } finally {
          confirm.setIsLoading(false);
        }
      },
      onCancel: () => {
        resolve(false);
      }
    });
  });
};

const errorTranslationBasePath = 'server_errors.organizations';

const handleAddOrganizationErrors = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  if (serverError.httpStatus === HttpStatusCode.BadRequest) {
    return getLocalizedString(`${errorTranslationBasePath}.create_failed`, {
      intl
    });
  }
});

const handleEditOrganizationErrors = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  if (serverError.httpStatus === HttpStatusCode.BadRequest) {
    return getLocalizedString(`${errorTranslationBasePath}.update_failed`, {
      intl
    });
  }
});

const handleDeleteOrganizationErrors = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  if (serverError.httpStatus === HttpStatusCode.BadRequest) {
    return getLocalizedString(`${errorTranslationBasePath}.delete_failed`, {
      intl
    });
  }
});

const handleAddDomainErrors = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  if (serverError.httpStatus === HttpStatusCode.Conflict) {
    return getLocalizedString(
      `${errorTranslationBasePath}.domain_add_duplicate_error`,
      {
        intl
      }
    );
  }
  if (
    serverError.httpStatus === HttpStatusCode.BadRequest ||
    isApi5xxResponseStatus(serverError.httpStatus)
  ) {
    const errorCode = serverError.error?.details?.[0]?.error_code;
    if (errorCode === AppErrors.INVALID_DOMAIN) {
      return getLocalizedString(
        `${errorTranslationBasePath}.invalid_domain_add_failed`,
        {
          intl
        }
      );
    } else if (errorCode === AppErrors.DISPOSABLE_OR_PUBLIC_EMAIL) {
      return getLocalizedString(
        `${errorTranslationBasePath}.disposable_public_email_domain_add_failed`,
        { intl }
      );
    }
    return getLocalizedString(`${errorTranslationBasePath}.domain_add_failed`, {
      intl
    });
  }
});

const handleAddAllowedEmailDomainErrors = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  if (serverError.httpStatus === HttpStatusCode.Conflict) {
    return getLocalizedString(
      `${errorTranslationBasePath}.domain_add_duplicate_error`,
      {
        intl
      }
    );
  }
  if (
    serverError.httpStatus === HttpStatusCode.BadRequest ||
    isApi5xxResponseStatus(serverError.httpStatus)
  ) {
    const errorCode = serverError.error?.details?.[0]?.error_code;

    switch (errorCode) {
      case AppErrors.DISPOSABLE_OR_PUBLIC_EMAIL:
        return getLocalizedString(
          `${errorTranslationBasePath}.disposable_public_email_domain_add_failed`,
          { intl }
        );

      case AppErrors.INVALID_DOMAIN:
        return getLocalizedString(
          `${errorTranslationBasePath}.invalid_domain_add_failed`,
          { intl }
        );

      case AppErrors.FSA_NOT_ENABLED:
        return getLocalizedString(
          `${errorTranslationBasePath}.domain_fsa_not_enabled_add_failed`,
          { intl }
        );

      default:
        return getLocalizedString(
          `${errorTranslationBasePath}.domain_add_failed`,
          {
            intl
          }
        );
    }
  }
});

const handleDeleteDomainErrors = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  if (
    serverError.httpStatus === HttpStatusCode.BadRequest ||
    isApi5xxResponseStatus(serverError.httpStatus)
  ) {
    return getLocalizedString(
      `${errorTranslationBasePath}.domain_delete_failed`,
      {
        intl
      }
    );
  }
});

const handleUpdateOrgUserLimitErrors = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  if (serverError.httpStatus === HttpStatusCode.BadRequest) {
    const errorCode = serverError.error?.details?.[0]?.error_code;

    if (errorCode === AppErrors.MAX_ALLOWED_USERS_LIMIT_REACHED) {
      return getLocalizedString(
        `${errorTranslationBasePath}.max_allowed_user_limit_reached`,
        {
          intl
        }
      );
    }

    return getLocalizedString(
      `${errorTranslationBasePath}.org_user_limit_update_failed`,
      {
        intl
      }
    );
  }
});

type OrganizationFeatureType = {
  feature: FeatureFlagKeys;
  defaultEnabled: boolean;
};

const ORGANIZATION_FEATURES: OrganizationFeatureType[] = [
  {
    feature: FeatureFlagKeys.SINGLE_SIGNON,
    defaultEnabled: false
  },
  {
    feature: FeatureFlagKeys.DIRECTORY_SYNC,
    defaultEnabled: false
  }
];

const getEnabledOrgFeatures = (): OrganizationFeatureType[] =>
  ORGANIZATION_FEATURES.filter(({ feature }) => isFeatureEnabled(feature));

export {
  getTranslationKey,
  getOrgRolesTranslationKey,
  ORG_LIST_PAGE_SIZE,
  ORGANIZATION_TAB_TYPES,
  DEFAULT_MAX_USERS_IN_ORGANIZATION,
  MIN_MAXIMUM_USERS_LIMIT_IN_ORGANIZATION,
  deleteOrganisationWithConfirmation,
  getOrgDomainsFromDomainData,
  getAllowedEmailDomainsFromDomainData,
  shouldSearch,
  getOrgListColumnDef,
  isTestOrganization,
  getTestConnectionFromConnectionList,
  handleAddOrganizationErrors,
  handleAddAllowedEmailDomainErrors,
  handleEditOrganizationErrors,
  handleDeleteOrganizationErrors,
  handleAddDomainErrors,
  handleDeleteDomainErrors,
  handleUpdateOrgUserLimitErrors,
  ORGANIZATION_FEATURES,
  getEnabledOrgFeatures,
  onDeleteAllowedEmailDomain
};
